import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Credenciales from './Credenciales';
import Administradores from './Administradores';
import Logs from './Logs';
import Estadisticas from './Estadisticas';
import ModalCredencial from './ModalCredencial';

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('credenciales');
  const { user, logout, token } = useAuth(); // 👈 AGREGAR 'token' aquí

  // Estados para el modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCredencial, setEditingCredencial] = useState(null);
  const [refreshCredenciales, setRefreshCredenciales] = useState(false);

  // Estado para tiempo restante de sesión (opcional)
  const [timeRemaining, setTimeRemaining] = useState(null);

  // Efecto para verificar tiempo restante del token (opcional)
  React.useEffect(() => {
    if (!token) return;

    const updateTimeRemaining = () => {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const exp = payload.exp * 1000;
        const remaining = exp - Date.now();

        if (remaining <= 0) {
          setTimeRemaining(null);
          return;
        }

        const minutes = Math.floor(remaining / 60000);
        const seconds = Math.floor((remaining % 60000) / 1000);

        if (remaining < 300000) {
          // menos de 5 minutos
          setTimeRemaining({ minutes, seconds, remaining });
        } else {
          setTimeRemaining(null);
        }
      } catch {
        setTimeRemaining(null);
      }
    };

    updateTimeRemaining();
    const interval = setInterval(updateTimeRemaining, 1000);
    return () => clearInterval(interval);
  }, [token]);

  const tabs = [
    {
      id: 'credenciales',
      label: 'Credenciales',
      icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
    },
    {
      id: 'estadisticas',
      label: 'Estadísticas',
      icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
    },
    {
      id: 'administradores',
      label: 'Administradores',
      icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z',
    },
    {
      id: 'logs',
      label: 'Logs de Acceso',
      icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
    },
  ];

  // Funciones para manejar el modal
  const handleOpenCreateModal = () => {
    setEditingCredencial(null);
    setModalOpen(true);
  };

  const handleOpenEditModal = (credencial) => {
    setEditingCredencial(credencial);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditingCredencial(null);
  };

  const handleModalSuccess = () => {
    setRefreshCredenciales((prev) => !prev);
    handleModalClose();
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'credenciales':
        return (
          <Credenciales
            onOpenCreateModal={handleOpenCreateModal}
            onOpenEditModal={handleOpenEditModal}
            refreshTrigger={refreshCredenciales}
          />
        );
      case 'estadisticas':
        return <Estadisticas />;
      case 'administradores':
        return <Administradores />;
      case 'logs':
        return <Logs />;
      default:
        return (
          <Credenciales
            onOpenCreateModal={handleOpenCreateModal}
            onOpenEditModal={handleOpenEditModal}
            refreshTrigger={refreshCredenciales}
          />
        );
    }
  };

  const userName = user?.nombre_completo || user?.username || 'Administrador';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Fondo decorativo animado */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-80 h-80 bg-blue-500 rounded-full blur-3xl opacity-15 animate-pulse"></div>
        <div
          className="absolute bottom-20 right-10 w-80 h-80 bg-orange-500 rounded-full blur-3xl opacity-15 animate-pulse"
          style={{ animationDelay: '1s' }}
        ></div>
      </div>

      {/* Banner superior con gradiente */}
      <div className="relative z-30 bg-gradient-to-r from-gray-800 via-blue-700 to-gray-900 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
                Panel de Administración
              </h1>
              <p className="text-white/80 text-base md:text-lg">
                Gestiona credenciales y administradores del sistema
              </p>
            </div>
            <div className="flex items-center space-x-4">
              {/* Indicador de tiempo restante - OPCIONAL */}
              {timeRemaining && (
                <div className="px-3 py-1 bg-yellow-500/20 border border-yellow-500/50 rounded-lg">
                  <span className="text-yellow-200 text-sm">
                    ⏰ Expira: {timeRemaining.minutes}:
                    {String(timeRemaining.seconds).padStart(2, '0')}
                  </span>
                </div>
              )}

              <div className="hidden md:flex items-center space-x-3 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-xl border border-white/20">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center shadow-md">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    ></path>
                  </svg>
                </div>
                <span className="font-medium text-white/90">{userName}</span>
              </div>
              <button
                onClick={logout}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white font-medium rounded-xl transition-all duration-300 hover:scale-105 flex items-center space-x-2 border border-white/10"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                  />
                </svg>
                <span>Cerrar sesión</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="sticky top-0 z-20 bg-black/30 backdrop-blur-md border-y border-white/10 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-1 overflow-x-auto py-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`group relative px-4 py-2.5 rounded-xl font-medium text-sm transition-all duration-300 whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'text-white bg-gradient-to-r from-blue-500/40 to-blue-600/40 shadow-lg shadow-blue-500/20'
                    : 'text-white/60 hover:text-white hover:bg-white/10'
                }`}
              >
                <span className="flex items-center space-x-2">
                  <svg
                    className={`w-4 h-4 transition-all duration-300 ${
                      activeTab === tab.id
                        ? 'text-blue-300'
                        : 'group-hover:text-blue-300'
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d={tab.icon}
                    />
                  </svg>
                  <span>{tab.label}</span>
                </span>

                {activeTab === tab.id && (
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-gradient-to-r from-blue-400 to-blue-500 rounded-full"></span>
                )}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {renderContent()}
      </main>

      {/* MODAL */}
      <ModalCredencial
        isOpen={modalOpen}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
        credencial={editingCredencial}
      />

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeInUp {
          animation: fadeInUp 0.5s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default Dashboard;
